import React from 'react';
import { Helmet } from 'react-helmet';
import { HiArrowLeft, HiOutlineDownload, HiOutlinePrinter } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { contactsData } from '../../../data/contactsData';
import { certificationsData, educationData } from '../../../data/educationData';
import { experienceData } from '../../../data/experienceData';
import { headerData } from '../../../data/headerData';
import { projectsData } from '../../../data/projectsData';
import { skillsData } from '../../../data/skillsData';
import { downloadResumeJson } from '../../../utils/jsonResume';
import Navbar from '../../container/navbar';
import './resume.css';

const PROJECT_LIMIT = 6;
const period = (item) => [item.startYear, item.endYear].filter(Boolean).join(' – ');
const bare = (url) => url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

function ResumeUI() {
    const links = [
        ['GitHub', contactsData.github],
        ['LinkedIn', contactsData.linkedIn],
    ].filter(([, url]) => url);

    return (
        <>
            <Helmet>
                <title>{`Résumé | ${headerData.name}`}</title>
                <meta name='description' content={`Résumé of ${headerData.name}, ${headerData.role}: experience, skills, projects and education.`} />
                <link rel='canonical' href='https://miretu.vercel.app/resume' />
            </Helmet>
            <Navbar />
            <main id='main' className='resume'>
                <div className='container'>
                    <div className='resume__actions'>
                        <Link to='/' className='resume__back'><HiArrowLeft aria-hidden='true' /> Portfolio</Link>
                        <span className='resume__buttons'>
                            <button type='button' className='btn' onClick={() => window.print()}>
                                <HiOutlinePrinter aria-hidden='true' /> Print or save as PDF
                            </button>
                            <button type='button' className='btn' onClick={() => downloadResumeJson()}>
                                <HiOutlineDownload aria-hidden='true' /> Download JSON
                            </button>
                        </span>
                    </div>

                    <article className='resume__paper'>
                        <header className='resume__head'>
                            <h1>{headerData.name}</h1>
                            <p className='resume__role'>{headerData.role}, {headerData.company}</p>
                            <p className='resume__contact'>
                                <a href={`mailto:${contactsData.email}`}>{contactsData.email}</a>
                                {contactsData.phone && <span>{contactsData.phone}</span>}
                                <span>{contactsData.address}</span>
                                {links.map(([label, url]) => (
                                    <a key={label} href={url} target='_blank' rel='noreferrer' aria-label={label}>{bare(url)}</a>
                                ))}
                            </p>
                        </header>

                        <section aria-labelledby='r-summary'>
                            <h2 id='r-summary'>Summary</h2>
                            <p>{headerData.intro}</p>
                        </section>

                        <section aria-labelledby='r-experience'>
                            <h2 id='r-experience'>Experience</h2>
                            {experienceData.map((job) => (
                                <div key={job.id} className='resume__item'>
                                    <div className='resume__row'>
                                        <h3>{job.jobtitle}, {job.company}</h3>
                                        {period(job) && <span>{period(job)}</span>}
                                    </div>
                                    {job.summary && <p>{job.summary}</p>}
                                    {job.highlights && job.highlights.length > 0 && (
                                        <ul>{job.highlights.map((text) => <li key={text}>{text}</li>)}</ul>
                                    )}
                                </div>
                            ))}
                        </section>

                        <section aria-labelledby='r-skills'>
                            <h2 id='r-skills'>Skills</h2>
                            <dl className='resume__skills'>
                                {skillsData.map((group) => (
                                    <div key={group.category}>
                                        <dt>{group.category}</dt>
                                        <dd>{group.items.join(', ')}</dd>
                                    </div>
                                ))}
                            </dl>
                        </section>

                        <section aria-labelledby='r-projects'>
                            <h2 id='r-projects'>Selected projects</h2>
                            {projectsData.slice(0, PROJECT_LIMIT).map((project) => (
                                <div key={project.id} className='resume__item'>
                                    <h3>{project.projectName}</h3>
                                    <p>{project.projectDesc}</p>
                                    {project.tags && project.tags.length > 0 && (
                                        <p className='resume__tags'>{project.tags.join(' · ')}</p>
                                    )}
                                </div>
                            ))}
                        </section>

                        <section aria-labelledby='r-education'>
                            <h2 id='r-education'>Education</h2>
                            {educationData.map((item) => (
                                <div key={item.id} className='resume__item'>
                                    <div className='resume__row'>
                                        <h3>{item.course}, {item.institution}</h3>
                                        {period(item) && <span>{period(item)}</span>}
                                    </div>
                                    {item.detail && <p>{item.detail}</p>}
                                </div>
                            ))}
                            {certificationsData.length > 0 && (
                                <>
                                    <h3 className='resume__sub'>Certifications</h3>
                                    <ul>{certificationsData.map((cert) => <li key={cert}>{cert}</li>)}</ul>
                                </>
                            )}
                        </section>

                        <section aria-labelledby='r-languages'>
                            <h2 id='r-languages'>Languages</h2>
                            <p>{headerData.languages}</p>
                        </section>
                    </article>
                </div>
            </main>
        </>
    );
}

export default ResumeUI;
